import { ImageScaleView, View, type ViewOpts } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';

/** Sleeping-pet decoration revealing a configurable number of Zs before an empty step. */
export default class DreamDriftSleepZ extends View {
  /** Z images arranged along a gently rising line above the sleeping pet. */
  private readonly zViews: ImageScaleView[];

  /** Duration of each animation step, including the empty step, in milliseconds. */
  private readonly interval: number;

  /** Visible animation time used to advance and repeat the complete cycle. */
  private elapsed = 0;

  /**
   * Create the sleeping decoration with one Z visible initially.
   *
   * @param opts Parent, position, base view options, and animation settings.
   * @param opts.amount Maximum number of Zs; defaults to 4.
   * @param opts.interval Duration of each step in milliseconds; defaults to 500.
   */
  constructor(opts: ViewOpts & { amount?: number; interval?: number } = {}) {
    // Separate the animation settings from the standard view options.
    const { amount = 4, interval = 500, ...viewOpts } = opts;

    // Preserve the source artwork's proportions at native game resolution.
    const zWidth = downscale(45);
    const zHeight = downscale(53);

    // Let the normal view lifecycle pause the animation while hidden or detached.
    super({
      width: zWidth * amount,
      height: zHeight + 10,
      canHandleEvents: false,
      ...viewOpts,
    });

    // Keep the configured interval for each animation step.
    this.interval = interval;

    // Place each subsequent Z closer and higher to match the artwork's tilt.
    this.zViews = Array.from(
      { length: amount },
      (_, index) =>
        new ImageScaleView({
          superview: this,
          image: 'assets/ui/dreamDrift/common/z.png',
          width: zWidth,
          height: zHeight,
          x: (zWidth - 4) * index,
          y: 10 - index * 6,
          visible: index === 0,
          scaleMethod: 'contain',
          canHandleEvents: false,
        }),
    );
  }

  /**
   * Reveal Zs incrementally, then show an empty step while the component is visible.
   *
   * @param delta Elapsed frame time in milliseconds.
   * @returns Nothing.
   */
  public tick(delta: number): void {
    // Reveal up to the configured number of Zs, then hide them before repeating.
    this.elapsed += delta;
    const visibleCount =
      (Math.floor(this.elapsed / this.interval) + 1) % (this.zViews.length + 1);

    // Change only letters whose visibility differs from the current count.
    for (const [index, z] of this.zViews.entries()) {
      const visible = index < visibleCount;
      if (z.style.visible === visible) continue;
      z.updateOpts({ visible });
    }
  }
}
